import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { BarraProgreso } from '#components/mobile/ui/BarraProgreso';
import { Boton } from '#components/mobile/ui/Boton';
import { Cargando } from '#components/mobile/ui/Cargando';
import { color, radio, TACTIL, texto } from '#components/mobile/ui/tokens';
import { pedirPuente } from '#uso';

import { Seccion } from './piezas';

type NivelParte = 'bien' | 'ojo' | 'mal';

type MensajeVigia = {
  id: number;
  ts: number;
  rol: 'user' | 'assistant';
  texto: string;
  herramientas: string[];
};

export type EstadoVigia = {
  encendido: boolean;
  hayClave: boolean;
  gasto: {
    mes: number;
    dia: number;
    topes: { mes: number; dia: number; pregunta: number };
  };
  parte: { ts: number; dia: string; nivel: NivelParte; texto: string } | null;
  mensajes: MensajeVigia[];
};

const LARGO_PREGUNTA = 2000;

const estiloCampo: CSSProperties = {
  width: '100%',
  minHeight: TACTIL + 4,
  borderRadius: radio.boton,
  backgroundColor: color.surface2,
  border: `1px solid ${color.line}`,
  padding: '12px 14px',
  fontSize: 16,
  lineHeight: 1.4,
  color: color.fg,
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  outline: 'none',
  resize: 'vertical',
};

const cifras: CSSProperties = { fontVariantNumeric: 'tabular-nums' };

/**
 * El Vigía (Vadik, 30-oct-2026): el ayudante del servidor, solo para el admin.
 * Enseña el parte de la ronda de la mañana, lo gastado de la API y una
 * conversación para preguntarle por el servidor. Solo lee: lo que haya que
 * cambiar lo da como comando para pegar o como informe para Claude.
 */
export function Vigia() {
  const { t } = useTranslation();
  const [estado, setEstado] = useState<EstadoVigia | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pregunta, setPregunta] = useState('');
  const [trabajando, setTrabajando] = useState<'mensaje' | 'ronda' | null>(
    null,
  );

  useEffect(() => {
    let vivo = true;
    pedirPuente<EstadoVigia>('/vigia')
      .then(e => vivo && setEstado(e))
      .catch((e: Error) => vivo && setError(e.message));
    return () => {
      vivo = false;
    };
  }, []);

  async function recargar() {
    setEstado(await pedirPuente<EstadoVigia>('/vigia'));
  }

  async function enviar() {
    const texto = pregunta.trim();
    if (!texto || trabajando) {
      return;
    }
    setTrabajando('mensaje');
    setError(null);
    // Se ve en seguida, antes de que conteste.
    setEstado(e =>
      e
        ? {
            ...e,
            mensajes: [
              ...e.mensajes,
              { id: -1, ts: 0, rol: 'user', texto, herramientas: [] },
            ],
          }
        : e,
    );
    setPregunta('');
    try {
      await pedirPuente('/vigia/mensaje', {
        metodo: 'POST',
        cuerpo: { texto },
      });
    } catch (e) {
      setError((e as Error).message);
      setPregunta(texto);
    }
    await recargar().catch((e: Error) => setError(e.message));
    setTrabajando(null);
  }

  async function ronda() {
    setTrabajando('ronda');
    setError(null);
    try {
      await pedirPuente('/vigia/ronda', { metodo: 'POST' });
    } catch (e) {
      setError((e as Error).message);
    }
    await recargar().catch((e: Error) => setError(e.message));
    setTrabajando(null);
  }

  async function cambiar(ruta: '/vigia/encendido' | '/vigia/nueva') {
    try {
      setEstado(
        await pedirPuente<EstadoVigia>(ruta, {
          metodo: 'POST',
          cuerpo:
            ruta === '/vigia/encendido'
              ? { encendido: !estado?.encendido }
              : {},
        }),
      );
    } catch (e) {
      setError((e as Error).message);
    }
  }

  if (!estado) {
    return error ? <AvisoError motivo={error} /> : <Cargando />;
  }

  const { gasto } = estado;

  return (
    <View style={{ gap: 12 }} data-testid="vigia">
      <Seccion
        titulo={t("This morning's report")}
        derecha={
          <Boton
            variante="fantasma"
            isDisabled={!!trabajando || !estado.hayClave || !estado.encendido}
            onPress={() => void ronda()}
          >
            {trabajando === 'ronda' ? t('Looking…') : t('Look now')}
          </Boton>
        }
      >
        {!estado.hayClave ? (
          <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
            <Trans>
              The lookout is asleep: the Claude API key is missing on the
              server.
            </Trans>
          </Text>
        ) : estado.parte ? (
          <Parte parte={estado.parte} />
        ) : (
          <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
            <Trans>No report yet. The first round is at 6:00.</Trans>
          </Text>
        )}
        <View style={{ gap: 4 }}>
          <Text style={{ ...texto.secundario, color: color.fg2, ...cifras }}>
            {t(
              'Spent this month: {{mes}} $ of {{topeMes}} $ · today {{dia}} $ of {{topeDia}} $',
              {
                mes: gasto.mes.toFixed(2),
                topeMes: gasto.topes.mes,
                dia: gasto.dia.toFixed(2),
                topeDia: gasto.topes.dia,
              },
            )}
          </Text>
          <BarraProgreso
            valor={gasto.mes / gasto.topes.mes}
            color={
              gasto.mes >= gasto.topes.mes * 0.8 ? color.warn : color.accent
            }
            aria-label={t('Spent this month')}
          />
        </View>
      </Seccion>

      <Seccion
        titulo={t('Ask the lookout')}
        derecha={
          estado.mensajes.length > 0 ? (
            <Boton
              variante="fantasma"
              isDisabled={!!trabajando}
              onPress={() => void cambiar('/vigia/nueva')}
            >
              <Trans>New chat</Trans>
            </Boton>
          ) : null
        }
      >
        {estado.mensajes.length === 0 && (
          <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
            <Trans>
              Ask about the server in plain words: «Is everything all right?»,
              «Why is it slow?», «Did last night's backup work?». I only look:
              if something needs changing, I tell you how.
            </Trans>
          </Text>
        )}
        {estado.mensajes.map((m, i) => (
          <Burbuja key={m.id === -1 ? `nuevo-${i}` : m.id} mensaje={m} />
        ))}
        {trabajando === 'mensaje' && (
          <Text style={{ ...texto.secundario, color: color.fg3 }}>
            <Trans>Looking at the server…</Trans>
          </Text>
        )}
        {error && <AvisoError motivo={error} />}
        <textarea
          data-testid="vigia-pregunta"
          aria-label={t('Ask the lookout')}
          value={pregunta}
          maxLength={LARGO_PREGUNTA}
          rows={2}
          placeholder={t('Ask something…')}
          disabled={!estado.hayClave || !estado.encendido}
          onChange={e => setPregunta(e.target.value)}
          style={estiloCampo}
        />
        <Boton
          bloque
          isDisabled={
            !pregunta.trim() ||
            !!trabajando ||
            !estado.hayClave ||
            !estado.encendido
          }
          onPress={() => void enviar()}
        >
          <Trans>Ask</Trans>
        </Boton>
      </Seccion>

      <Seccion
        titulo={t('Lookout')}
        derecha={
          <Boton
            variante={estado.encendido ? 'fantasma' : 'primario'}
            onPress={() => void cambiar('/vigia/encendido')}
            data-testid="vigia-interruptor"
          >
            {estado.encendido ? t('Turn off') : t('Turn on')}
          </Boton>
        }
      >
        <Text style={{ ...texto.secundario, color: color.fg2 }}>
          {estado.encendido ? (
            <Trans>
              On. He only reads: never budgets (they are encrypted) or the
              server keys. What he reads goes to Anthropic so Claude can think
              about it.
            </Trans>
          ) : (
            <Trans>Off. He doesn't look at anything or spend anything.</Trans>
          )}
        </Text>
      </Seccion>
    </View>
  );
}

function Parte({ parte }: { parte: NonNullable<EstadoVigia['parte']> }) {
  const { t } = useTranslation();
  const tonos = {
    bien: { fondo: color.okSoft, tinta: color.ok, etiqueta: t('All good') },
    ojo: { fondo: color.warnSoft, tinta: color.warn, etiqueta: t('Heads up') },
    mal: { fondo: color.badSoft, tinta: color.bad, etiqueta: t('Act today') },
  };
  const tono = tonos[parte.nivel] ?? tonos.bien;
  return (
    <View style={{ gap: 6 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Text
          style={{
            ...texto.pequeno,
            backgroundColor: tono.fondo,
            color: tono.tinta,
            borderRadius: 999,
            padding: '3px 10px',
          }}
        >
          {tono.etiqueta}
        </Text>
        <Text style={{ ...texto.secundario, color: color.fg3 }}>
          {new Date(parte.ts * 1000).toLocaleString(undefined, {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </Text>
      </View>
      <Text
        style={{ ...texto.cuerpo, color: color.fg, whiteSpace: 'pre-wrap' }}
      >
        {parte.texto}
      </Text>
    </View>
  );
}

function Burbuja({ mensaje }: { mensaje: MensajeVigia }) {
  const { t } = useTranslation();
  const mio = mensaje.rol === 'user';
  const nombres: Record<string, string> = {
    estado: t('the machine'),
    registros: t('the logs'),
    registro_vm: t('the system logs'),
    copias: t('the backups'),
    configuracion: t('the settings'),
    imagenes: t('the versions'),
    rumbo_por_dentro: t('Rumbo inside'),
    leer_manual: t('the manual'),
    mi_gasto: t('my spending'),
  };
  const miradas = [...new Set(mensaje.herramientas)]
    .map(h => nombres[h] ?? h)
    .join(', ');
  return (
    <View
      style={{
        alignSelf: mio ? 'flex-end' : 'flex-start',
        maxWidth: '88%',
        gap: 4,
      }}
    >
      {!mio && miradas && (
        <Text style={{ ...texto.secundario, color: color.fg3 }}>
          {t('Looked at: {{miradas}}', { miradas })}
        </Text>
      )}
      <Text
        style={{
          ...texto.cuerpo,
          whiteSpace: 'pre-wrap',
          padding: '9px 12px',
          borderRadius: 16,
          backgroundColor: mio ? color.accent : color.surface2,
          color: mio ? color.accentInk : color.fg,
        }}
      >
        {mensaje.texto}
      </Text>
    </View>
  );
}

function AvisoError({ motivo }: { motivo: string }) {
  const { t } = useTranslation();
  const textos: Record<string, string> = {
    forbidden: t('Only the person who runs Rumbo can see this.'),
    'sin-clave': t(
      'The lookout is asleep: the Claude API key is missing on the server.',
    ),
    'tope-mes': t(
      "The lookout has reached this month's spending limit. He will be back next month.",
    ),
    'tope-dia': t(
      "The lookout has reached today's spending limit. He will be back tomorrow.",
    ),
    ocupado: t('The lookout is busy with another question. Try in a moment.'),
    apagado: t('The lookout is off.'),
  };
  return (
    <Text style={{ ...texto.secundario, color: color.bad }}>
      {textos[motivo] ??
        t('The lookout could not answer. Try again in a while.')}
    </Text>
  );
}
